import { createFileRoute } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import {
  CheckCircle2,
  History,
  Loader2,
  MapPin,
  Plus,
  RefreshCw,
  StopCircle,
  X,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { LoadingSpinner } from '@/components/loading-spinner'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { useAccounts } from '@/lib/use-accounts'
import { useSearchStatus, useStopSearch } from '@/lib/api'
import { VehicleOptimizationSection } from '@/components/vehicle-optimization-section'
import { localizeDigits } from '@/lib/localize-digits'

export const Route = createFileRoute('/search_/status')({
  component: SearchStatusPage,
})

// Individual Search Status Card Component
function SearchStatusCard({
  account,
}: {
  account: { id: string; name: string; email?: string }
}) {
  const { t } = useTranslation()
  const stopSearchMutation = useStopSearch()
  const searchStatus = useSearchStatus(account.id, true)

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return localizeDigits(
      new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(date),
    )
  }

  const formatUptime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const secs = Math.floor(seconds % 60)

    if (hours > 0) {
      return localizeDigits(`${hours}h ${minutes}m ${secs}s`)
    } else if (minutes > 0) {
      return localizeDigits(`${minutes}m ${secs}s`)
    } else {
      return localizeDigits(`${secs}s`)
    }
  }

  const getStatusBadge = (status: string) => {
    const statusLower = status.toLowerCase()
    const variants = {
      running: 'default',
      completed: 'default',
      stopped: 'secondary',
      error: 'destructive',
    } as const

    const variant = variants[statusLower as keyof typeof variants]

    return (
      <Badge variant={variant} className="text-xs">
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    )
  }

  const handleStopSearch = async () => {
    try {
      await stopSearchMutation.mutateAsync({ accountName: account.id })
      toast.success(t('search.searchStopped') || 'Search stopped successfully')
    } catch (error) {
      console.error('Failed to stop search:', error)
      toast.error(t('search.stopSearchFailed') || 'Failed to stop search.')
    }
  }

  // If loading, show loading state
  if (searchStatus.isLoading) {
    return (
      <Card className="hover:bg-accent/50 transition-colors">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">{account.name}</CardTitle>
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            {t('common.loading')}...
          </p>
        </CardContent>
      </Card>
    )
  }

  // If error or no data, show no active search
  if (searchStatus.isError || !searchStatus.data) {
    return (
      <Card className="hover:bg-accent/50 transition-colors">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">{account.name}</CardTitle>
            <Badge variant="secondary" className="text-xs">
              {t('search.noActiveSearch') || 'No active search'}
            </Badge>
          </div>
          <p className="text-xs text-muted-foreground mt-2">{account.email}</p>
        </CardContent>
      </Card>
    )
  }

  const data = searchStatus.data
  const isRunning = data.status.toLowerCase() === 'running'
  const metrics = data.metrics

  return (
    <Card className="hover:bg-accent/50 transition-colors">
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <CardTitle className="text-base">{account.name}</CardTitle>
              {getStatusBadge(data.status || 'unknown')}
              {isRunning && (
                <Badge
                  variant="default"
                  className="text-xs bg-yellow-600 animate-pulse"
                >
                  <Loader2 className="h-3 w-3 me-1 animate-spin" />
                  {t('search.searching') || 'Searching...'}
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground mb-2">
              {t('search.startedAt') || 'Started at'}:{' '}
              {formatDate(data.start_time)}
            </p>
            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
              <MapPin className="h-3 w-3" />
              <span>
                {t('search.radiusLabel')}:{' '}
                {localizeDigits(data.params.radius.toFixed(2))} km
              </span>
            </div>
          </div>
        </div>

        {/* Search Parameters */}
        <div className="mb-3">
          <p className="text-xs font-medium mb-1">
            {t('search.parameters') || 'Parameters'}:
          </p>
          <div className="flex flex-wrap gap-1">
            {data.params.filters &&
            Object.keys(data.params.filters).length > 0 ? (
              Object.entries(data.params.filters).map(([name, enabled]) =>
                enabled ? (
                  <Badge key={name} variant="outline" className="text-xs">
                    {name
                      .split('_')
                      .map(
                        (word) => word.charAt(0).toUpperCase() + word.slice(1),
                      )
                      .join(' ')}
                  </Badge>
                ) : null,
              )
            ) : (
              <span className="text-xs text-muted-foreground">
                {t('search.noFiltersEnabled') || 'No filters enabled'}
              </span>
            )}
          </div>
        </div>

        {/* Metrics */}
        {metrics && (
          <div className="mb-3 p-3 bg-muted/50 rounded-lg">
            <p className="text-xs font-medium mb-2">
              {t('search.metrics') || 'Metrics'}:
            </p>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>
                <span className="text-muted-foreground">
                  {t('search.uptime') || 'Uptime'}:
                </span>
                <span className="font-medium ms-1">
                  {formatUptime(metrics.uptime_seconds || 0)}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">
                  {t('search.requests') || 'Requests'}:
                </span>
                <span className="font-medium ms-1">
                  {localizeDigits(String(metrics.upstream_requests || 0))}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">
                  {t('search.evaluated') || 'Evaluated'}:
                </span>
                <span className="font-medium ms-1">
                  {localizeDigits(String(metrics.vehicles_evaluated || 0))}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">
                  {t('search.skipped') || 'Skipped'}:
                </span>
                <span className="font-medium ms-1">
                  {localizeDigits(String(metrics.skipped_vehicles || 0))}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">
                  {t('search.attempts') || 'Booking Attempts'}:
                </span>
                <span className="font-medium ms-1">
                  {localizeDigits(String(metrics.booking_attempts || 0))}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">
                  {t('search.errors') || 'Errors'}:
                </span>
                <span className="font-medium ms-1">
                  {localizeDigits(String(metrics.upstream_errors || 0))}
                </span>
              </div>
            </div>
            {metrics.last_error && (
              <div className="mt-2 p-2 bg-destructive/10 rounded text-xs text-destructive">
                <span className="font-medium">
                  {t('search.lastError') || 'Last Error'}:
                </span>{' '}
                {metrics.last_error}
              </div>
            )}
            {Object.keys(metrics.skipped_by_reason).length > 0 && (
              <div className="mt-2">
                <p className="text-xs font-medium mb-1">
                  {t('search.skippedReasons') || 'Skipped by reason'}:
                </p>
                <div className="flex flex-wrap gap-1">
                  {Object.entries(metrics.skipped_by_reason).map(
                    ([reason, count]) => (
                      <Badge key={reason} variant="outline" className="text-xs">
                        {reason}: {localizeDigits(String(count))}
                      </Badge>
                    ),
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Result */}
        {data.result && (
          <div
            className={`p-3 rounded-lg mb-3 ${data.result.is_success ? 'bg-green-500/10' : 'bg-destructive/10'}`}
          >
            <div className="flex items-center gap-2 mb-1">
              {data.result.is_success ? (
                <CheckCircle2 className="h-4 w-4 text-green-600" />
              ) : (
                <X className="h-4 w-4 text-destructive" />
              )}
              <span className="text-xs font-medium">
                {data.result.is_success
                  ? t('search.searchSuccess') || 'Car found and reserved!'
                  : t('search.searchFailed') || 'Search failed'}
              </span>
            </div>
            {data.result.is_success && data.result.rental_id && (
              <p className="text-xs text-muted-foreground">
                {t('search.rentalId') || 'Rental ID'}:{' '}
                {localizeDigits(String(data.result.rental_id))}
              </p>
            )}
            {data.result.error && (
              <p className="text-xs text-destructive mt-1">
                {data.result.error}
              </p>
            )}
            {data.result.is_cancelled_previous_rental && (
              <p className="text-xs text-muted-foreground mt-1">
                {t('search.cancelledPrevious') ||
                  'Previous rental was cancelled'}
              </p>
            )}
          </div>
        )}

        {/* Actions */}
        {isRunning && (
          <Button
            className="w-full"
            size="sm"
            variant="destructive"
            onClick={handleStopSearch}
            disabled={stopSearchMutation.isPending}
          >
            {stopSearchMutation.isPending ? (
              <Loader2 className="h-4 w-4 me-2 animate-spin" />
            ) : (
              <StopCircle className="h-4 w-4 me-2" />
            )}
            {t('search.stopSearch') || 'Stop Search'}
          </Button>
        )}
      </CardContent>
    </Card>
  )
}

// Wrapper component that shows both search status and optimization
function AccountSearchSection({
  account,
}: {
  account: { id: string; name: string; email?: string }
}) {
  const searchStatus = useSearchStatus(account.id, true)

  // Only show optimization section if search completed successfully
  const showOptimization =
    searchStatus.data?.status === 'completed' &&
    searchStatus.data.result?.is_success === true

  return (
    <div>
      <SearchStatusCard account={account} />
      {showOptimization && searchStatus.data && (
        <VehicleOptimizationSection
          accountName={account.id}
          searchStatus={searchStatus.data}
        />
      )}
    </div>
  )
}

function SearchStatusPage() {
  const { t } = useTranslation()
  const { accounts: accountsList, isLoading: accountsLoading } = useAccounts()
  const [countdown, setCountdown] = useState(15)
  const [isRefreshing, setIsRefreshing] = useState(false)

  // Update countdown every second
  useEffect(() => {
    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          return 15 // Reset to 15 when it reaches 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(interval)
  }, [])

  const handleRefresh = () => {
    setIsRefreshing(true)
    setCountdown(15) // Reset countdown on manual refresh

    // Force all search status queries to refetch
    // We'll trigger this by updating a timestamp that components can react to
    setTimeout(() => {
      window.location.reload()
    }, 100)
  }

  return (
    <div className="fixed-fullscreen-safe fixed overflow-y-auto">
      <div className="container mx-auto max-w-screen-md px-4 py-4">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold mb-1">
              {t('search.searchStatus.title') || 'Search Status'}
            </h1>
            <p className="text-muted-foreground text-sm">
              {t('search.searchStatus.description') ||
                'Monitor all account searches in real-time'}
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={handleRefresh}
              size="icon"
              variant="outline"
              disabled={isRefreshing}
            >
              {isRefreshing ? (
                <RefreshCw className="h-5 w-5 animate-spin" />
              ) : (
                <div className="flex flex-col items-center justify-center">
                  <span className="text-xs font-bold">
                    {localizeDigits(String(countdown))}
                  </span>
                  <span className="text-[8px] leading-none">sec</span>
                </div>
              )}
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={() => (window.location.href = '/search')}
            >
              <Plus className="h-5 w-5" />
            </Button>
          </div>
        </div>

        {accountsLoading && (
          <Card>
            <CardContent className="p-8">
              <LoadingSpinner size="lg" />
            </CardContent>
          </Card>
        )}

        {!accountsLoading && accountsList.length === 0 && (
          <Card>
            <CardContent className="p-8 text-center">
              <History className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground">
                {t('search.noAccounts') || 'No accounts available'}
              </p>
            </CardContent>
          </Card>
        )}

        {!accountsLoading && accountsList.length > 0 && (
          <div className="space-y-3">
            {accountsList.map((account) => (
              <AccountSearchSection key={account.id} account={account} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
